// Per-variant registry: theme tokens + nav + CTA + brand override + chrome.
//
// Used by `resources/js/Layouts/AppLayout.vue` so that interior pages
// (Förderung-Finder, Ratgeber, Kontakt, …) keep the look-and-feel of
// the active design variant when a user navigates away from the
// homepage. The activeVariant slug arrives via Inertia shared props
// (see app/Http/Middleware/HandleInertiaRequests.php).
//
// React Nav/Footer live in chrome.js and are loaded only when
// VariantChrome.vue actually mounts a vNN page. AppLayout must NOT
// import those JSX files — that crashed every Vue homepage in Vite
// dev (plugin-react preamble) and left #ssr-lcp as the only paint.

export const VARIANT_CHROME_SLUGS = [
    'v01_trustbank', 'v02_editorial', 'v03_earthy', 'v04_modernist',
    'v05_technical', 'v06_softconsumer', 'v07_swiss', 'v08_cinematic',
    'v09_directresponse', 'v10_mapled', 'v11_forum', 'v12_conversational',
    'v13_handwerker', 'v14_comparison',
];

/** Truthy slug map for AppLayout's useVariantChrome check — no JSX. */
export const VARIANT_CHROME = Object.fromEntries(
    VARIANT_CHROME_SLUGS.map((slug) => [slug, true]),
);

export const VARIANT_REGISTRY = {
  // ────────────────────────────────────────────────────────────
  // React variants (resources/js/Variants/_v0X_*.jsx)
  // ────────────────────────────────────────────────────────────
  v01_trustbank: {
    name: 'Trust-Bank Serious',
    theme: {
      bg: '#f7f4ee', fg: '#1a2236', muted: '#5a6378',
      border: '#d9d3c5', accent: '#1f3a5f', accentFg: '#f7f4ee',
      inputBg: '#ffffff', radius: 4,
      font: '"Source Serif 4", Georgia, serif',
    },
    nav: [
      { label: 'Ratgeber', href: '/ratgeber' },
      { label: 'So funktioniert es', href: '/so-funktioniert-es' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Kontakt', href: '/kontakt' },
    ],
    cta: { label: 'Angebote vergleichen', href: '/angebot-vergleichen' },
    brand: null,
  },

  v02_editorial: {
    name: 'Editorial',
    theme: {
      bg: '#fbfaf7', fg: '#0f0f0f', muted: '#6b6660',
      border: '#e6e1d6', accent: '#a8341c', accentFg: '#ffffff',
      inputBg: '#fff', radius: 2,
      font: '"Playfair Display", "Times New Roman", serif',
    },
    nav: [
      { label: 'Ratgeber', href: '/ratgeber' },
      { label: 'Marktanalyse', href: '/marktanalyse' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Kontakt', href: '/kontakt' },
    ],
    cta: { label: 'Angebot anfordern', href: '/angebot-vergleichen' },
    brand: null,
  },

  v03_earthy: {
    name: 'Earthy',
    theme: {
      bg: '#ece6d8', fg: '#2b2820', muted: '#6e6a5c',
      border: '#d6cfbb', accent: '#5b6b46', accentFg: '#fbfaf3',
      inputBg: '#fbfaf3', radius: 14,
      font: '"DM Sans", system-ui, sans-serif',
    },
    nav: [
      { label: 'Wärmepumpe', href: '/wärmepumpe' },
      { label: 'Solar', href: '/photovoltaik' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Über uns', href: '/über-uns' },
    ],
    cta: { label: 'Angebote vergleichen', href: '/angebot-vergleichen' },
    brand: null,
  },

  v04_modernist: {
    name: 'Modernist',
    theme: {
      bg: '#f4f3ef', fg: '#0a0a0a', muted: '#5a5a5a',
      border: '#0a0a0a', accent: '#ff5b1f', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 0,
      font: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    },
    nav: [
      { label: 'Tools', href: '/tools-wissen' },
      { label: 'Marktanalyse', href: '/marktanalyse' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Ratgeber', href: '/ratgeber' },
    ],
    cta: { label: 'Angebote vergleichen', href: '/angebot-vergleichen' },
    brand: null,
  },

  v05_technical: {
    name: 'Technical',
    theme: {
      bg: '#f5f6f7', fg: '#101418', muted: '#5b6470',
      border: '#dfe3e8', accent: '#005ea3', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 4,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: [
      { label: 'Methode', href: '/so-funktioniert-es' },
      { label: 'Marktanalyse', href: '/marktanalyse' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Kontakt', href: '/kontakt' },
    ],
    cta: { label: 'Jetzt vergleichen', href: '/angebot-vergleichen' },
    brand: null,
  },

  v06_softconsumer: {
    name: 'Soft Consumer',
    theme: {
      bg: '#fff8ee', fg: '#3a2a1c', muted: '#7a6655',
      border: '#f0e4d0', accent: '#e07a3a', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 16,
      font: '"Nunito", system-ui, sans-serif',
    },
    nav: [
      { label: 'Solar', href: '/photovoltaik' },
      { label: 'Wärmepumpe', href: '/wärmepumpe' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Kontakt', href: '/kontakt' },
    ],
    cta: { label: 'Angebote erhalten', href: '/angebot-vergleichen' },
    brand: null,
  },

  v07_swiss: {
    name: 'Swiss',
    theme: {
      bg: '#fafaf7', fg: '#0a0a0a', muted: '#6a6a6a',
      border: '#0a0a0a', accent: '#d62828', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 0,
      font: '"Inter", "Neue Haas Grotesk", "Helvetica Neue", Helvetica, sans-serif',
    },
    nav: [
      { label: 'Methode', href: '/so-funktioniert-es' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Marktanalyse', href: '/marktanalyse' },
      { label: 'Kontakt', href: '/kontakt' },
    ],
    cta: { label: 'Angebote vergleichen', href: '/angebot-vergleichen' },
    brand: 'Waemo',
  },

  v08_cinematic: {
    name: 'Cinematic',
    theme: {
      bg: '#0d1117', fg: '#f3f0ea', muted: '#9fa3aa',
      border: 'rgba(255,255,255,0.14)', accent: '#f3c969', accentFg: '#0d1117',
      inputBg: 'rgba(255,255,255,0.06)', radius: 8,
      font: '"Playfair Display", "Source Serif 4", Georgia, serif',
    },
    nav: [
      { label: 'Anlagen', href: '/produkte' },
      { label: 'Solarrechner', href: '/solarrechner' },
      { label: 'Wärmepumpe', href: '/wärmepumpe' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Magazin', href: '/ratgeber' },
    ],
    cta: { label: 'Angebote vergleichen', href: '/angebot-vergleichen' },
    brand: null,
  },

  v09_directresponse: {
    name: 'Direct Response',
    theme: {
      bg: '#0b0b0b', fg: '#fafafa', muted: '#a3a3a3',
      border: '#2a2a2a', accent: '#fde047', accentFg: '#0b0b0b',
      inputBg: '#171717', radius: 4,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: [
      { label: 'Solar', href: '/photovoltaik' },
      { label: 'Vorteile', href: '/vorteile' },
      { label: 'Wärmepumpe', href: '/wärmepumpe' },
      { label: 'Förderung', href: '/förderung-finder' },
    ],
    cta: { label: 'Jetzt vergleichen', href: '/angebot-vergleichen' },
    brand: null,
  },

  v10_mapled: {
    name: 'Map Led',
    theme: {
      bg: '#f4f4f0', fg: '#1c1f1c', muted: '#5c625b',
      border: '#d8d6cd', accent: '#2d6a4f', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 8,
      font: '"DM Sans", system-ui, sans-serif',
    },
    nav: [
      { label: 'Marktanalyse', href: '/marktanalyse' },
      { label: 'Partner', href: '/partner' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Kontakt', href: '/kontakt' },
    ],
    cta: { label: 'Angebote vergleichen', href: '/angebot-vergleichen' },
    brand: 'WAEMO Energie',
  },

  v11_forum: {
    name: 'Forum',
    theme: {
      bg: '#f8f7f2', fg: '#1a1a1a', muted: '#6b6b6b',
      border: '#e3e0d4', accent: '#15803d', accentFg: '#ffffff',
      inputBg: '#fff', radius: 6,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: [
      { label: 'Forum', href: '/forum' },
      { label: 'Marktanalyse', href: '/marktanalyse' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Glossar', href: '/glossar' },
    ],
    cta: { label: 'Anfrage starten', href: '/angebot-vergleichen' },
    brand: null,
  },

  v12_conversational: {
    name: 'Conversational',
    theme: {
      bg: '#0f1419', fg: '#f4f3ee', muted: '#9aa0aa',
      border: 'rgba(255,255,255,0.1)', accent: '#7cf5b9', accentFg: '#0d1117',
      inputBg: '#1a1d24', radius: 14,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: [
      { label: 'Solar', href: '/photovoltaik' },
      { label: 'Wärmepumpe', href: '/wärmepumpe' },
      { label: 'Marktanalyse', href: '/marktanalyse' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Magazin', href: '/ratgeber' },
    ],
    cta: { label: 'Chat starten', href: '/angebot-vergleichen' },
    brand: null,
  },

  v13_handwerker: {
    name: 'Handwerker',
    theme: {
      bg: '#1a1612', fg: '#f5efe1', muted: '#a99c84',
      border: 'rgba(245, 239, 225, 0.15)', accent: '#d4a44e', accentFg: '#1a1612',
      inputBg: '#231d17', radius: 4,
      font: '"Playfair Display", "Times New Roman", serif',
    },
    nav: [
      { label: 'Über uns', href: '/über-uns' },
      { label: 'Wärmepumpe', href: '/wärmepumpe' },
      { label: 'Photovoltaik', href: '/photovoltaik' },
      { label: 'Termin', href: '/kontakt' },
    ],
    cta: { label: 'Termin vereinbaren', href: '/kontakt' },
    brand: null,
  },

  v14_comparison: {
    name: 'Comparison',
    theme: {
      bg: '#fafbfc', fg: '#0f172a', muted: '#64748b',
      border: '#e4e7eb', accent: '#10b981', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 8,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: [
      { label: 'Vergleich', href: '/angebot-vergleichen' },
      { label: 'Methode', href: '/so-funktioniert-es' },
      { label: 'Förderung', href: '/förderung-finder' },
      { label: 'Kontakt', href: '/kontakt' },
    ],
    cta: { label: 'Vergleich starten', href: '/angebot-vergleichen' },
    brand: 'WärmePreis',
  },

  // ────────────────────────────────────────────────────────────
  // Vue-side variants (resources/js/Pages/{slug}/Home.vue)
  // These already render via AppLayout — registry entries here keep
  // the same chrome but ensure the theme/nav still apply when the
  // user navigated in via ?variant=<slug>.
  // ────────────────────────────────────────────────────────────
  waemo: {
    name: 'Waemo',
    theme: {
      bg: '#fbfaf6', fg: '#1A1A17', muted: '#615f57',
      border: '#e7e3d6', accent: '#d97706', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 8,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: null,
    cta: null,
    brand: 'WAEMO',
  },

  profi: {
    name: 'Profi',
    theme: {
      bg: '#f5f5f3', fg: '#15171a', muted: '#5a5e64',
      border: '#dcdcd6', accent: '#0d6efd', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 6,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: null,
    cta: null,
    brand: null,
  },

  studivolt: {
    name: 'StudiVolt',
    theme: {
      bg: '#fffdf8', fg: '#1a1a1a', muted: '#5e5e5e',
      border: '#e8e3d6', accent: '#16a34a', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 12,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: null,
    cta: null,
    brand: 'StudiVolt',
  },

  rittinger: {
    name: 'Rittinger',
    theme: {
      bg: '#fafaf7', fg: '#1a1a1a', muted: '#666',
      border: '#e0e0d8', accent: '#b91c1c', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 4,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: null,
    cta: null,
    brand: null,
  },

  standard: {
    name: 'Standard / Brand-Home',
    theme: {
      bg: '#ffffff', fg: '#111111', muted: '#666',
      border: '#e5e5e5', accent: '#0f6b3a', accentFg: '#ffffff',
      inputBg: '#ffffff', radius: 8,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: null,
    cta: null,
    brand: null,
  },

  ai: {
    name: 'AI Welcome',
    theme: {
      bg: '#0a0a0f', fg: '#fafafa', muted: '#a3a3a3',
      border: 'rgba(255,255,255,0.1)', accent: '#8b5cf6', accentFg: '#ffffff',
      inputBg: '#171723', radius: 12,
      font: '"Inter", system-ui, sans-serif',
    },
    nav: null,
    cta: null,
    brand: null,
  },
};

// Lookup helper. Returns the registry entry for a slug, or null.
// Nav/Footer React components are not attached here — import chrome.js
// from VariantChrome.vue when a vNN page actually needs them.
export function getVariantConfig(slug) {
  if (!slug || typeof slug !== 'string') return null;
  return VARIANT_REGISTRY[slug] || null;
}

// Map a registry theme (the inline-style shape used by React variants)
// onto the same CSS-var keys that AppLayout's `themeStyles` already
// consumes, so swapping in a variant only requires Object.assign-ing
// the result over the existing styles.
export function variantThemeToCssVars(theme) {
  if (!theme) return {};
  const vars = {
    '--brand-accent': theme.accent,
    '--brand-accent-soft': hexToRgba(theme.accent, 0.14),
    '--brand-accent-text': theme.accentFg,
    '--brand-accent-fg': isLightHex(theme.accent) ? '#1a1a1a' : theme.accent,
    '--dv-page': theme.bg,
    '--dv-section': theme.bg,
    '--dv-section-alt': theme.bg,
    '--dv-card': theme.inputBg || '#ffffff',
    '--dv-card-alt': theme.inputBg || '#ffffff',
    '--dv-text': theme.fg,
    '--dv-muted': theme.muted,
    '--dv-border': theme.border,
    '--dv-dark': isLightHex(theme.bg) ? '#111827' : theme.bg,
    '--bg-cream': theme.bg,
    '--bg-surface': theme.inputBg || '#ffffff',
    '--border-warm': theme.border,
    '--border-soft': theme.border,
    '--ink': theme.fg,
    '--ink-mute': theme.muted,
    background: theme.bg,
    color: theme.fg,
  };
  if (theme.font) {
    vars.fontFamily = theme.font;
    vars['--theme-font-body'] = theme.font;
    vars['--theme-font-heading'] = theme.font;
    vars['--dv-font-body'] = theme.font;
    vars['--dv-font-heading'] = theme.font;
  }
  return vars;
}

function hexToRgba(input, alpha) {
  if (!input || typeof input !== 'string') return null;
  const m = input.replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(m)) return input;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function isLightHex(input) {
  if (!input || typeof input !== 'string') return false;
  const m = input.replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(m)) return false;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55;
}
